import {
  MAX_TRANSCRIPT_BYTES,
  TRANSCRIPT_CHANNEL,
  peerMessageSchema,
  type IceCandidateInit,
  type IceConfig,
  type PeerMessage,
  type SessionDescription,
} from '@voxa/shared';

/**
 * The browser-to-browser connection. One audio track out (this microphone),
 * one in (the peer's *original* voice, which the translator will consume).
 *
 * Perfect negotiation, with the initiator role handed down by the server so
 * both sides cannot decide they are polite at the same moment. Seat 0 offers;
 * seat 1 yields on a collision.
 */

export type PeerStatus = 'new' | 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'closed';

export interface PeerHandlers {
  onRemoteTrack(track: MediaStreamTrack, stream: MediaStream): void;
  /** Subtitles the peer's own translator produced. Never touches the server. */
  onPeerMessage(message: PeerMessage): void;
  onLocalDescription(description: SessionDescription): void;
  onLocalCandidate(candidate: IceCandidateInit): void;
  onStatus(status: PeerStatus): void;
}

export class PeerLink {
  readonly #pc: RTCPeerConnection;
  readonly #polite: boolean;
  readonly #handlers: PeerHandlers;
  #makingOffer = false;
  #ignoreOffer = false;
  #sender: RTCRtpSender | null = null;
  #transcripts: RTCDataChannel | null = null;
  #closed = false;

  constructor(iceConfig: IceConfig, isInitiator: boolean, handlers: PeerHandlers) {
    this.#polite = !isInitiator;
    this.#handlers = handlers;
    this.#pc = new RTCPeerConnection({
      iceServers: iceConfig.iceServers,
      iceTransportPolicy: iceConfig.iceTransportPolicy,
      bundlePolicy: 'max-bundle',
    });

    // The initiator opens it so it is in the first offer; the polite peer
    // picks it up from `datachannel`. Both ends then have the same channel
    // without a second negotiation.
    if (isInitiator) {
      this.#adoptChannel(this.#pc.createDataChannel(TRANSCRIPT_CHANNEL, { ordered: true }));
    }
    this.#pc.addEventListener('datachannel', (event) => {
      if (event.channel.label === TRANSCRIPT_CHANNEL) this.#adoptChannel(event.channel);
    });

    this.#pc.addEventListener('track', (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.#handlers.onRemoteTrack(event.track, stream);
    });

    this.#pc.addEventListener('icecandidate', (event) => {
      // Trickle ICE. The null candidate marks the end of gathering and is not
      // worth a round trip.
      const init = event.candidate?.toJSON();
      // `candidate` is optional in the DOM type but always present on a real
      // one; the schema requires it, so an empty string would be a lie.
      if (init?.candidate) {
        this.#handlers.onLocalCandidate({
          candidate: init.candidate,
          sdpMid: init.sdpMid ?? null,
          sdpMLineIndex: init.sdpMLineIndex ?? null,
          usernameFragment: init.usernameFragment ?? null,
        });
      }
    });

    this.#pc.addEventListener('negotiationneeded', () => {
      void this.#negotiate();
    });

    this.#pc.addEventListener('connectionstatechange', () => {
      switch (this.#pc.connectionState) {
        case 'connected':
          this.#handlers.onStatus('connected');
          break;
        case 'connecting':
          this.#handlers.onStatus('connecting');
          break;
        case 'disconnected':
          this.#handlers.onStatus('reconnecting');
          break;
        case 'failed':
          this.#handlers.onStatus('failed');
          // An ICE restart is the cheap fix for a route that died; only the
          // offerer may start one.
          if (!this.#polite) void this.restartIce();
          break;
        case 'closed':
          this.#handlers.onStatus('closed');
          break;
        default:
          break;
      }
    });
  }

  /**
   * The microphone. Added once with its stream, then swapped in place — a
   * `replaceTrack` needs no renegotiation, so changing device mid-call costs
   * nothing.
   *
   * `addTrack` rather than `addTransceiver` + `replaceTrack`: it is what gives
   * the m-line an msid, which is what puts a stream in the peer's `ontrack`
   * event. Section 5 wants the directions settled before anyone speaks, and
   * this runs before signalling connects, so they are.
   */
  async setMicrophoneTrack(track: MediaStreamTrack | null): Promise<void> {
    if (this.#closed) return;
    if (this.#sender) {
      await this.#sender.replaceTrack(track);
      return;
    }
    if (track) {
      this.#sender = this.#pc.addTrack(track, new MediaStream([track]));
    }
  }

  /**
   * Hands one subtitle fragment to the peer. Best effort by design: a dropped
   * caption is not worth an error, and the channel may not be open yet while
   * the connection is still coming up.
   */
  sendMessage(message: PeerMessage): void {
    if (this.#transcripts?.readyState !== 'open') return;
    try {
      this.#transcripts.send(JSON.stringify(message));
    } catch {
      // The channel closed between the check and the send. Nothing to do.
    }
  }

  #adoptChannel(channel: RTCDataChannel): void {
    this.#transcripts = channel;
    channel.addEventListener('message', (event: MessageEvent) => {
      if (typeof event.data !== 'string' || event.data.length > MAX_TRANSCRIPT_BYTES) return;
      let json: unknown;
      try {
        json = JSON.parse(event.data);
      } catch {
        return;
      }
      // The peer is another browser, not a trusted service. Its frames are
      // validated exactly like the server's.
      const parsed = peerMessageSchema.safeParse(json);
      if (parsed.success) this.#handlers.onPeerMessage(parsed.data);
    });
  }

  async acceptDescription(description: SessionDescription): Promise<void> {
    if (this.#closed) return;
    const offerCollision =
      description.type === 'offer' && (this.#makingOffer || this.#pc.signalingState !== 'stable');

    // The impolite peer keeps its own offer and drops the incoming one; the
    // polite peer rolls back. Exactly one side yields, so the exchange always
    // converges.
    this.#ignoreOffer = !this.#polite && offerCollision;
    if (this.#ignoreOffer) return;

    await this.#pc.setRemoteDescription(description as RTCSessionDescriptionInit);
    if (description.type === 'offer') {
      await this.#pc.setLocalDescription();
      const local = this.#pc.localDescription;
      if (local) this.#handlers.onLocalDescription({ type: 'answer', sdp: local.sdp });
    }
  }

  async acceptCandidate(candidate: IceCandidateInit): Promise<void> {
    if (this.#closed) return;
    try {
      await this.#pc.addIceCandidate(candidate as RTCIceCandidateInit);
    } catch (err) {
      // A candidate that arrives for an offer we chose to ignore is expected
      // noise, not a fault.
      if (!this.#ignoreOffer) throw err;
    }
  }

  /**
   * Offer, now. The constructor's `negotiationneeded` fires the moment the
   * transceiver is added — which, for the person who created the room, is
   * long before anyone is on the other end to receive it. That first offer
   * goes into a socket with no peer behind it and is simply lost, and nothing
   * ever fires `negotiationneeded` again. So the caller re-offers when the
   * peer actually shows up, and after a reconnect for the same reason.
   *
   * A no-op for the polite peer: exactly one side offers.
   */
  async start(): Promise<void> {
    await this.#negotiate();
  }

  async restartIce(): Promise<void> {
    if (this.#closed || this.#polite) return;
    this.#pc.restartIce();
    await this.#negotiate();
  }

  /** Fresh TURN credentials without tearing the call down. */
  updateIceServers(iceConfig: IceConfig): void {
    if (this.#closed) return;
    this.#pc.setConfiguration({
      iceServers: iceConfig.iceServers,
      iceTransportPolicy: iceConfig.iceTransportPolicy,
      bundlePolicy: 'max-bundle',
    });
  }

  /** Section 10.2: the selected route, RTT and loss, straight from the source. */
  async stats(): Promise<{
    rtt: number | null;
    jitter: number | null;
    loss: number | null;
    route: string | null;
  }> {
    const out = {
      rtt: null as number | null,
      jitter: null as number | null,
      loss: null as number | null,
      route: null as string | null,
    };
    if (this.#closed) return out;
    const report = await this.#pc.getStats();
    report.forEach((stat) => {
      if (stat.type === 'candidate-pair' && (stat as RTCIceCandidatePairStats).nominated) {
        const pair = stat as RTCIceCandidatePairStats;
        out.rtt = pair.currentRoundTripTime !== undefined ? pair.currentRoundTripTime * 1000 : null;
      }
      if (stat.type === 'inbound-rtp' && (stat as RTCInboundRtpStreamStats).kind === 'audio') {
        const rtp = stat as RTCInboundRtpStreamStats;
        out.jitter = rtp.jitter !== undefined ? rtp.jitter * 1000 : null;
        out.loss = rtp.packetsLost ?? null;
      }
      if (stat.type === 'local-candidate') {
        const c = stat as RTCIceCandidate & { candidateType?: string };
        if (c.candidateType === 'relay') out.route = 'relay';
        else if (out.route === null) out.route = c.candidateType ?? null;
      }
    });
    return out;
  }

  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    try {
      this.#transcripts?.close();
    } catch {
      /* already gone */
    }
    this.#transcripts = null;
    // The microphone goes quiet here. It belongs to the caller, but a call
    // that has ended must not leave the recording light on while the page
    // waits to be collected.
    for (const sender of this.#pc.getSenders()) sender.track?.stop();
    this.#pc.close();
    this.#handlers.onStatus('closed');
  }

  async #negotiate(): Promise<void> {
    if (this.#closed || this.#polite) return;
    try {
      this.#makingOffer = true;
      await this.#pc.setLocalDescription();
      const local = this.#pc.localDescription;
      if (local) this.#handlers.onLocalDescription({ type: 'offer', sdp: local.sdp });
    } finally {
      this.#makingOffer = false;
    }
  }
}
